import { Fragment, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";

export type MenuSelectOption = {
  value: string;
  label: string;
  /** Secondary line under the label inside the popover; never shown on the trigger. */
  detail?: string;
  /** Right-aligned status text inside the popover (capacity, readiness…). */
  meta?: string;
  /** Consecutive options sharing a group render under one heading. */
  group?: string;
  /**
   * Extra text the search matches but never renders — model ids, provider ids.
   * Someone who knows the catalogue types `opus-5`, not the display name.
   */
  keywords?: string;
  /** Identity tile drawn before the label inside the popover row (a repository's icon). */
  icon?: ReactNode;
  disabled?: boolean;
};

/**
 * A borderless menu button plus an anchored, optionally searchable option list.
 *
 * The trigger's width is driven by `minWidth`/`maxWidth` and its label truncates,
 * so picking a longer option never reflows the row it sits in — the whole point
 * of using this instead of a native `<select>` in the chrome rows.
 *
 * Passing `selectedValues` makes it a multi-select: `onChange` is called with
 * the option that was toggled, every selected option carries a tick, and the
 * popover stays open so a set can be assembled in one gesture. Everything else
 * — the search, the keyboard cursor, the pinned trigger width — is the same
 * control, because a repository picker that behaved differently from the agent
 * picker beside it would be a second idiom for no reason.
 */
export function MenuSelect({
  value,
  selectedValues,
  options,
  onChange,
  ariaLabel,
  placeholder = "Select…",
  searchPlaceholder,
  searchable = false,
  disabled = false,
  mono = false,
  leading,
  caption,
  triggerLabel,
  minWidth = 140,
  maxWidth = 260,
  align = "start",
  size = "md",
  className,
  title,
}: {
  value: string;
  /** Present for a multi-select; `onChange` then toggles rather than replaces. */
  selectedValues?: readonly string[];
  options: MenuSelectOption[];
  onChange: (value: string) => void;
  ariaLabel: string;
  placeholder?: string;
  searchPlaceholder?: string;
  searchable?: boolean;
  disabled?: boolean;
  mono?: boolean;
  /** Identity tile / provider mark rendered before the label on the trigger. */
  leading?: ReactNode;
  /** Quiet prefix inside the trigger, e.g. "Agent" or "from". */
  caption?: string;
  /** What the trigger says when it should not just repeat the selected option's label. */
  triggerLabel?: string;
  minWidth?: number;
  maxWidth?: number;
  align?: "start" | "end";
  size?: "sm" | "md";
  className?: string;
  title?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  /**
   * Hover moves the keyboard cursor, and the keyboard cursor scrolls itself
   * into view. Together that means resting the pointer near a clipped row
   * scrolls the list under the pointer, which lands a *different* row under it,
   * which scrolls again. Only the keyboard gets to scroll.
   */
  const cursorFromPointer = useRef(false);

  const multiple = selectedValues !== undefined;
  const isSelected = (option: MenuSelectOption) =>
    multiple ? selectedValues.includes(option.value) : option.value === value;
  const selected = options.find((option) => option.value === value);
  // A multi-select with a set chosen is not showing a placeholder, whatever its
  // single `value` happens to be.
  const hasSelection = multiple ? selectedValues.length > 0 : selected !== undefined;
  // Every whitespace-separated term has to match somewhere, so "opus fast"
  // narrows rather than widening the way a single-substring match would.
  const visible = useMemo(() => {
    const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (terms.length === 0) return options;
    return options.filter((option) => {
      const haystack = [option.label, option.detail, option.group, option.keywords]
        .filter(Boolean).join(" ").toLowerCase();
      return terms.every((term) => haystack.includes(term));
    });
  }, [options, search]);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    const index = options.findIndex((option) => isSelected(option));
    setActiveIndex(index < 0 ? 0 : index);
    if (searchable) searchRef.current?.focus();
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [search]);

  // Close on outside pointer-down. Capture phase so a click that also opens
  // another menu resolves in one gesture instead of leaving both open.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown, true);
    return () => document.removeEventListener("mousedown", onPointerDown, true);
  }, [open]);

  // Escape has to close only this menu. The surrounding Modal listens for it on
  // `window` in the bubble phase, so swallow it here in the capture phase — a
  // React-level stopPropagation would be at the mercy of portal ordering.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      rootRef.current?.querySelector<HTMLButtonElement>(".menu-select-trigger")?.focus();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (cursorFromPointer.current) {
      cursorFromPointer.current = false;
      return;
    }
    listRef.current?.querySelector<HTMLElement>('[data-active="true"]')?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  function commit(option: MenuSelectOption | undefined) {
    if (!option || option.disabled) return;
    onChange(option.value);
    // A multi-select is a set being assembled: closing after the first tick
    // would make choosing three repositories three round trips.
    if (multiple) return;
    setOpen(false);
    rootRef.current?.querySelector<HTMLButtonElement>(".menu-select-trigger")?.focus();
  }

  function move(delta: number) {
    cursorFromPointer.current = false;
    if (visible.length === 0) return;
    let next = activeIndex;
    for (let step = 0; step < visible.length; step += 1) {
      next = (next + delta + visible.length) % visible.length;
      if (!visible[next]?.disabled) break;
    }
    setActiveIndex(next);
  }

  function onListKeyDown(event: { key: string; preventDefault: () => void }) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      move(1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      move(-1);
    } else if (event.key === "Enter") {
      event.preventDefault();
      commit(visible[activeIndex]);
    }
  }

  return (
    <div className={`menu-select${className ? ` ${className}` : ""}`} ref={rootRef}>
      <button
        type="button"
        className={`menu-select-trigger menu-select-${size}${open ? " open" : ""}`}
        style={{ minWidth, maxWidth }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        title={title ?? triggerLabel ?? selected?.label ?? placeholder}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        {leading ? <span className="menu-select-leading" aria-hidden="true">{leading}</span> : null}
        {caption ? <span className="menu-select-caption">{caption}</span> : null}
        <span className={`menu-select-label${mono ? " mono" : ""}${hasSelection ? "" : " placeholder"}`}>
          {triggerLabel ?? selected?.label ?? placeholder}
        </span>
        <svg className="menu-select-chevron" viewBox="0 0 10 6" aria-hidden="true">
          <path d="M1 1.5 5 5l4-3.5" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open ? (
        <div className={`menu-select-popover menu-select-popover-${align}`} onKeyDown={onListKeyDown}>
          {searchable ? (
            <input
              ref={searchRef}
              className="menu-select-search"
              type="text"
              autoComplete="off"
              spellCheck={false}
              aria-label={`Search ${ariaLabel}`}
              aria-controls={listId}
              placeholder={searchPlaceholder ?? "Search…"}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          ) : null}
          <div className="menu-select-list" id={listId} role="listbox" aria-label={ariaLabel} aria-multiselectable={multiple || undefined} ref={listRef}>
            {visible.length === 0 ? <div className="menu-select-empty">No matches</div> : null}
            {visible.map((option, index) => (
              <Fragment key={option.value}>
              {option.group && option.group !== visible[index - 1]?.group ? (
                <div className="menu-select-group">{option.group}</div>
              ) : null}
              <button
                type="button"
                role="option"
                aria-selected={isSelected(option)}
                data-active={index === activeIndex}
                className={`menu-select-option${isSelected(option) ? " selected" : ""}`}
                disabled={option.disabled}
                onMouseEnter={() => {
                  if (index === activeIndex) return;
                  cursorFromPointer.current = true;
                  setActiveIndex(index);
                }}
                onClick={() => commit(option)}
              >
                {option.icon ? <span className="menu-select-leading" aria-hidden="true">{option.icon}</span> : null}
                <span className="menu-select-option-copy">
                  <span className={`menu-select-option-label${mono ? " mono" : ""}`}>{option.label}</span>
                  {option.detail ? <span className="menu-select-option-detail">{option.detail}</span> : null}
                </span>
                {option.meta ? <span className="menu-select-option-meta">{option.meta}</span> : null}
                {/* Fixed slot, always rendered: the tick appearing must not shift the label. */}
                <span className="menu-select-option-check" aria-hidden="true">
                  {isSelected(option) ? (
                    <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2.2 6.4 4.6 8.8 9.8 3.4" />
                    </svg>
                  ) : null}
                </span>
              </button>
              </Fragment>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
